import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchMemberById,
  fetchGroups,
  addMemberToGroup,
  removeMemberFromGroup,
  createGroup,
} from "../features/members/slice-extensions/members-slice-extension";
import {
  selectCurrentMember,
  selectMembersStatus,
  selectGroups,
} from "../features/members/members-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import { toast } from "react-toastify";

const MemberDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const member = useAppSelector(selectCurrentMember);
  const status = useAppSelector(selectMembersStatus);
  const allGroups = useAppSelector(selectGroups);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(
    null
  );
  const [groupSearchText, setGroupSearchText] = useState("");
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);

  // Groups available to add (not already assigned to this member)
  const availableGroups = useMemo(() => {
    const memberGroupIds = new Set(member?.groups?.map((g) => g.id) || []);
    return allGroups.filter((g) => {
      if (memberGroupIds.has(g.id)) return false;
      if (!groupSearchText.trim()) return true;
      return g.name.toLowerCase().includes(groupSearchText.toLowerCase());
    });
  }, [allGroups, member?.groups, groupSearchText]);

  const canCreateGroup = useMemo(() => {
    const trimmed = groupSearchText.trim();
    if (!trimmed) return false;
    return !allGroups.some(
      (g) => g.name.toLowerCase() === trimmed.toLowerCase()
    );
  }, [groupSearchText, allGroups]);

  useEffect(() => {
    if (id) {
      dispatch(fetchMemberById(id));
      dispatch(fetchGroups());
    }
  }, [id, dispatch]);

  const handleAddGroup = async (groupId: string) => {
    if (!id || isAddingGroup) return;
    setIsAddingGroup(true);
    try {
      const result = await dispatch(addMemberToGroup({ groupId, memberId: id }));
      if (addMemberToGroup.fulfilled.match(result)) {
        toast.success("Group added!");
        setGroupSearchText("");
        setShowGroupDropdown(false);
        dispatch(fetchMemberById(id));
      } else {
        toast.error("Failed to add group.");
      }
    } finally {
      setIsAddingGroup(false);
    }
  };

  const handleRemoveGroup = async (groupId: string) => {
    if (!id) return;
    try {
      const result = await dispatch(removeMemberFromGroup({ groupId, memberId: id }));
      if (removeMemberFromGroup.fulfilled.match(result)) {
        toast.success("Group removed.");
        dispatch(fetchMemberById(id));
      } else {
        toast.error("Failed to remove group.");
      }
    } catch {
      toast.error("Failed to remove group.");
    }
  };

  const handleCreateAndSelectGroup = async () => {
    const name = groupSearchText.trim();
    if (!name || isCreatingGroup || !id) return;
    setIsCreatingGroup(true);
    try {
      const result = await dispatch(
        createGroup({ name, description: null, tags: [] })
      );
      if (createGroup.fulfilled.match(result)) {
        const newGroup = result.payload;
        toast.success(`Group "${name}" created!`);
        // Immediately add the member to the new group
        const addResult = await dispatch(
          addMemberToGroup({ groupId: newGroup.id, memberId: id })
        );
        if (addMemberToGroup.fulfilled.match(addResult)) {
          toast.success("Member added to group!");
          dispatch(fetchMemberById(id));
        }
        setGroupSearchText("");
        setShowGroupDropdown(false);
        dispatch(fetchGroups());
      } else {
        toast.error("Failed to create group.");
      }
    } catch {
      toast.error("Failed to create group.");
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Handle 404 with countdown and redirect
  useEffect(() => {
    if (status === LoadingStatus.Failed) {
      setRedirectCountdown(3);

      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            navigate(ROUTES.MEMBERS);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [status, navigate]);

  const getInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return `${first}${last}`;
  };

  const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  const MEMBER_COLORS = [
    "#367D7D", "#2E6B6B", "#3D8F8F", "#2A7A5E",
    "#4A8C6F", "#3B7A9E", "#2D6E8A", "#458B74",
  ];

  const getMemberColor = (firstName: string, lastName: string): string => {
    return MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];
  };

  if (status === LoadingStatus.Loading) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <div className="text-center py-4">
                  <p className="text-grey-30 dark:text-grey-70">
                    Loading member...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (status === LoadingStatus.Failed) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <h1 className="text-grey-10 dark:text-white">
                  Member Not Found
                </h1>
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <p className="text-red-600 dark:text-red-400 mb-4">
                    The member you are looking for could not be found.
                  </p>
                  {redirectCountdown !== null && (
                    <p className="text-grey-30 dark:text-grey-70 mb-4">
                      Redirecting to members list in {redirectCountdown}...
                    </p>
                  )}
                  <div className="mt-4">
                    <button
                      onClick={() => navigate(ROUTES.MEMBERS)}
                      className="btn btn-secondary"
                    >
                      Go to Members
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!member) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <h1 className="text-grey-10 dark:text-white">
                  Member Not Found
                </h1>
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <p className="text-grey-30 dark:text-grey-70">
                    The member you are looking for does not exist.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate(ROUTES.MEMBERS)}
                      className="btn btn-secondary"
                    >
                      Go to Members
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            <Breadcrumbs />

            <div className="space-y-4 py-6 lg:space-y-6">
              {/* Header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h1 className="text-grey-10 dark:text-white">
                  Member Details
                </h1>
              </div>

              {/* Member Info Card */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <div className="flex items-center gap-4">
                  {/* Initials Avatar */}
                  <div
                    className="flex items-center justify-center rounded-full"
                    style={{
                      backgroundColor: getMemberColor(member.firstName, member.lastName),
                      width: 56,
                      height: 56,
                      minWidth: 56,
                      minHeight: 56,
                    }}
                  >
                    <span className="text-lg font-bold text-white">
                      {getInitials(member.firstName, member.lastName)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-grey-10 dark:text-white">
                      {member.firstName} {member.lastName}
                    </h2>
                    {member.email && (
                      <p className="text-grey-30 dark:text-grey-70 text-sm">
                        {member.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <hr className="border-grey-70 dark:border-primary-20" />

              {/* Member Groups */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-grey-10 dark:text-grey-90">
                    Member Groups ({member.groups?.length || 0})
                  </h3>
                </div>

                {member.groups && member.groups.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {member.groups.map((group) => (
                      <span
                        key={group.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-90 dark:bg-primary-20 text-primary-30 dark:text-primary-90 text-sm font-medium"
                      >
                        <Link
                          to={ROUTES.GROUP_DETAILS.replace(":id", group.id)}
                          className="cursor-pointer hover:underline"
                        >
                          {group.name}
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleRemoveGroup(group.id)}
                          className="text-grey-50 hover:text-red-500 dark:text-grey-70 dark:hover:text-red-400 ml-0.5"
                          title="Remove from group"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-grey-30 dark:text-grey-70 text-sm mb-4">
                    No groups assigned.
                  </p>
                )}

                {/* Add Group Search */}
                <div className="relative">
                  <input
                    type="text"
                    className="input"
                    placeholder="Search groups to add..."
                    value={groupSearchText}
                    onChange={(e) => {
                      setGroupSearchText(e.target.value);
                      setShowGroupDropdown(true);
                    }}
                    onFocus={() => setShowGroupDropdown(true)}
                    onBlur={() => {
                      setTimeout(() => setShowGroupDropdown(false), 200);
                    }}
                    disabled={isAddingGroup || isCreatingGroup}
                  />
                  {showGroupDropdown && (availableGroups.length > 0 || canCreateGroup) && (
                    <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-10 shadow-lg">
                      {availableGroups.map((group) => (
                        <button
                          key={group.id}
                          type="button"
                          className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                          onClick={() => handleAddGroup(group.id)}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {group.name}
                            </p>
                            {group.description && (
                              <p className="text-xs text-grey-50 dark:text-grey-70 truncate">
                                {group.description}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                      {canCreateGroup && (
                        <>
                          {availableGroups.length > 0 && (
                            <div className="border-t border-grey-70 dark:border-primary-20" />
                          )}
                          <button
                            type="button"
                            className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-primary-40 dark:text-primary-90 transition-colors"
                            onClick={handleCreateAndSelectGroup}
                            disabled={isCreatingGroup}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="text-sm font-medium">
                              {isCreatingGroup ? "Creating..." : `Create "${groupSearchText.trim()}"`}
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Divider */}
              <hr className="border-grey-70 dark:border-primary-20" />

              {/* Notes */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <h3 className="text-grey-10 dark:text-grey-90 mb-4">Notes</h3>
                {member.notes ? (
                  <p className="text-grey-20 dark:text-grey-80 whitespace-pre-wrap">
                    {member.notes}
                  </p>
                ) : (
                  <p className="text-grey-30 dark:text-grey-70 text-sm">
                    No data
                  </p>
                )}
              </div>

              {/* Edit Member Button */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    navigate(ROUTES.EDIT_MEMBER.replace(":id", member.id))
                  }
                  className="btn btn-primary"
                >
                  Edit Member
                </button>
                <button
                  onClick={() => navigate(ROUTES.MEMBERS)}
                  className="btn btn-secondary"
                >
                  Back to Members
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MemberDetailsPage;
